import React, { FC, ReactElement, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { checkStringMultipleIncludes } from '../../utils';
import { getMetaTags, TagsSet } from './metadata';
import { DEFAULT_THEME, PUBLIC_URL } from '../../constants';
import getSource from '../../utils/getSource';
import useConfig from '../../stores/config';
import { Loan } from '../../schema';
import { isPrequalPath } from '../../utils/prequalRouteHelper';

type Props = {
  loan: any;
};

const getAbsoluteFaviconUrl = (faviconUrl: string | undefined): string => {
  if (!faviconUrl) return DEFAULT_THEME.favicon;
  if (faviconUrl.startsWith('http://') || faviconUrl.startsWith('https://')) {
    return faviconUrl;
  }
  return `${PUBLIC_URL}${faviconUrl}`;
};

const HeadContent: FC<Props> = ({ loan }): ReactElement | null => {
  const config = useConfig();
  const { source } = getSource(config, loan as Loan);
  const sourceDisplayName = source?.displayName || 'Trident Funding LLC';

  const { pathname } = useLocation();
  const isNotALoan: boolean = checkStringMultipleIncludes(pathname, ['apps', 'profile']);
  const isPrequal: boolean = isPrequalPath(pathname);

  const pageName: string = isPrequal
    ? 'prequal'
    : isNotALoan || !loan || loan.currentStep === 'type'
    ? 'home'
    : loan.purpose.toLowerCase();

  const meta: TagsSet = getMetaTags({ page: pageName, sourceName: sourceDisplayName });

  // index.html ships a static <meta name="robots" content="noindex,nofollow">
  // as the fail-safe default for every route in this client-rendered SPA.
  // react-helmet only manages tags it originally rendered (data-react-helmet),
  // so it can't replace that static tag -- it would just add a second,
  // conflicting one, and the most-restrictive tag (noindex) wins. So we
  // remove the static tag once, up front, and let Helmet's own robots tag
  // (below) take over for every route -- its content is recomputed on every
  // render from the current `isPrequal`, so navigating away from prequal
  // (even client-side, without a full reload) correctly restores noindex.
  //
  // Must run before the `if (!meta) return null` below -- Hooks have to be
  // called unconditionally, in the same order, on every render.
  useEffect(() => {
    document.querySelector('meta[name="robots"]:not([data-react-helmet])')?.remove();
  }, []);

  if (!meta) return null;

  const faviconUrl = getAbsoluteFaviconUrl(source?.theme?.favicon);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={isPrequal ? 'index,follow' : 'noindex,nofollow'} />
      <link rel="icon" type="image/ico" href={faviconUrl} sizes="16x16" />
      <link rel="apple-touch-icon" href={faviconUrl} />
    </Helmet>
  );
};

export default HeadContent;
